import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { LanguageProvider } from "./lib/i18n";
import { IdentityProvider } from "./lib/identity";
import { ThemeProvider } from "./lib/theme";
import { ErrorBoundary } from "./components/snapdraw/error-boundary";
import { reportError } from "./lib/errorReporting";
import "./styles/theme.css";

// Basic observability (backlog ADMIN-9) — catches errors outside any
// component tree (event handlers, async code, module init); render-time
// exceptions are caught by <ErrorBoundary> below instead.
window.addEventListener("error", (event) => {
  void reportError({ message: event.message, stack: event.error?.stack, name: event.error?.name });
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  void reportError({
    message: reason instanceof Error ? reason.message : String(reason),
    stack: reason instanceof Error ? reason.stack : undefined,
    name: reason instanceof Error ? reason.name : "UnhandledRejection",
  });
});

/**
 * Auto-recover from a stale-deploy chunk-load failure (backlog ADMIN-17).
 * Every route is React.lazy(), which Vite compiles into a request for a
 * content-hashed chunk file — a tab left open across a deploy (this app
 * ships to Production several times a day) still holds the *old* hash,
 * and Cloudflare Pages no longer serves that file once a new deploy
 * replaces it. Vite dispatches this cancelable event before rethrowing;
 * calling preventDefault() suppresses that rethrow so it never reaches
 * <ErrorBoundary> at all — a one-time reload almost always fixes it
 * silently, since the *new* page load requests the *current* hash.
 *
 * CHUNK_RELOAD_FLAG guards against looping forever if this isn't actually
 * a stale-deploy issue (e.g. a real network/CDN outage, where reloading
 * won't help): if the same failure recurs within the window below, this
 * listener does nothing, and Vite's default rethrow proceeds exactly as
 * before this fix — landing on <ErrorBoundary>'s existing manual-reload
 * screen and its normal ADMIN-9 alert. The flag then resets after a short
 * delay (long enough to cover React Router resolving the failing chunk
 * again on the fresh page load, short enough that an unrelated failure
 * hours later in the same long-lived tab isn't mistaken for a repeat of
 * this one) — deliberately not tied to any component's mount, since
 * App.tsx itself lives in the main bundle and would "succeed" on every
 * reload regardless of whether the same lazy child chunk fails again.
 */
const CHUNK_RELOAD_FLAG = "snapdraw.chunkReloadAttempted";
const CHUNK_RELOAD_FLAG_RESET_MS = 10_000;

window.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
  event.preventDefault();
  window.location.reload();
});
setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_FLAG), CHUNK_RELOAD_FLAG_RESET_MS);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <LanguageProvider>
        <IdentityProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </BrowserRouter>
        </IdentityProvider>
      </LanguageProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
